import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

/**
 * Reveal — scroll-triggered fade + rise. Once only.
 */
export const Reveal = ({
  children,
  delay = 0,
  y = 28,
  duration = 0.8,
  className = "",
  as = "div",
}) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </MotionTag>
  );
};

/**
 * Stagger — reveals children sequentially when the group enters view.
 */
export const Stagger = ({ children, className = "", stagger = 0.09, delay = 0 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-70px" }}
    variants={{
      hidden: {},
      show: { transition: { staggerChildren: stagger, delayChildren: delay } },
    }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 30 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      variants={{
        hidden: reduce ? { opacity: 0 } : { opacity: 0, y },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.8, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

/**
 * MaskedLines — signature on-load headline reveal, line by line from a clip mask.
 * `lines` is an array of strings or nodes.
 */
export const MaskedLines = ({
  lines,
  className = "",
  lineClassName = "",
  stagger = 0.11,
  delay = 0.15,
  duration = 0.95,
  animateOnView = false,
}) => {
  const reduce = useReducedMotion();
  const anim = reduce ? { y: "0%" } : { y: "0%" };
  const init = reduce ? { y: "0%", opacity: 0 } : { y: "115%" };
  const shown = reduce ? { y: "0%", opacity: 1 } : { y: "0%" };

  const trigger = animateOnView
    ? { whileInView: shown, viewport: { once: true, margin: "-60px" } }
    : { animate: shown };

  return (
    <span className={className} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]" aria-hidden="true">
          <motion.span
            className={`block ${lineClassName}`}
            initial={init}
            {...trigger}
            transition={{ duration, delay: delay + i * stagger, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

/**
 * Parallax — vertical parallax tied to scroll progress through the viewport.
 */
export const Parallax = ({ children, offset = 60, className = "" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [offset, -offset]);
  return (
    <motion.div ref={ref} style={reduce ? undefined : { y }} className={className}>
      {children}
    </motion.div>
  );
};

/**
 * Marquee — slow editorial marquee. Duplicates content for a seamless loop.
 */
export const Marquee = ({ children, className = "" }) => (
  <div className={`relative flex overflow-hidden ${className}`}>
    <div className="marquee-track flex shrink-0 items-center whitespace-nowrap">
      {children}
    </div>
    <div
      className="marquee-track flex shrink-0 items-center whitespace-nowrap"
      aria-hidden="true"
    >
      {children}
    </div>
  </div>
);
